import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import { FiSmile, FiX } from 'react-icons/fi';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import PropTypes from 'prop-types';

const ModernEmojiPicker = ({ onEmojiSelect, message = '', setMessage }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [open, setOpen] = useState(false);
    const buttonRef = useRef(null);
    const popoverRef = useRef(null);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setAnchorEl(null);
    };

    const handleEmojiSelect = (emoji) => {
        if (typeof setMessage === 'function') {
            setMessage((prev) => `${prev}${emoji.native}`);
        } else if (typeof onEmojiSelect === 'function') {
            onEmojiSelect(emoji);
        }
        // Keep the picker open for multiple selections
    };

    // Improved click outside detection
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside both button and popover
            const isOutsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
            const isOutsidePopover = popoverRef.current && !popoverRef.current.contains(event.target);

            if (isOutsideButton && isOutsidePopover) {
                handleClose();
            }
        };

        if (open) {
            // Use a small delay to prevent immediate closing
            const timeoutId = setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 100);

            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [open]);

    return (
        <>
            <IconButton
                ref={buttonRef}
                onClick={handleClick}
                sx={{
                    color: open ? 'primary.main' : 'text.secondary',
                    p: 1,
                    borderRadius: '50%',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'primary.lighter',
                        color: 'primary.main'
                    }
                }}
            >
                <FiSmile size={18} />
            </IconButton>

            <Popover
                ref={popoverRef}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                disableRestoreFocus
                disableAutoFocus
                disableEnforceFocus
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        border: '1px solid #E2E8F0',
                        overflow: 'hidden',
                        '& .emoji-mart': {
                            border: 'none',
                            borderRadius: '12px'
                        },
                        '& .emoji-mart-search': {
                            border: 'none',
                            padding: '12px'
                        },
                        '& .emoji-mart-search input': {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            padding: '8px 12px',
                            fontSize: '14px'
                        },
                        '& .emoji-mart-category-label': {
                            backgroundColor: '#F8FAFC',
                            color: '#64748B',
                            fontSize: '12px',
                            fontWeight: 600,
                            padding: '8px 12px'
                        },
                        '& .emoji-mart-scroll': {
                            padding: '8px'
                        },
                        '& .emoji-mart-emoji': {
                            padding: '4px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#F1F5F9'
                            }
                        }
                    }
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1
                        }}
                    >
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: 'text.secondary',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                '&:hover': {
                                    backgroundColor: 'grey.200'
                                }
                            }}
                        >
                            <FiX size={14} />
                        </IconButton>
                    </Box>

                    <Picker
                        data={data}
                        onEmojiSelect={handleEmojiSelect}
                        theme="light"
                        set="native"
                        showPreview={false}
                        showSkinTones={false}
                        emojiSize={20}
                        maxFrequentRows={4}
                        style={{
                            width: '320px',
                            height: '400px'
                        }}
                    />
                </Box>
            </Popover>
        </>
    );
};

ModernEmojiPicker.propTypes = {
    onEmojiSelect: PropTypes.func,
    message: PropTypes.string,
    setMessage: PropTypes.func
};

export default ModernEmojiPicker;
